import {useCallback, useState, useMemo, useEffect} from 'react';

import {assert} from 'Asserts';
import {ChatType, AgentType, LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import {trackMessageSent} from 'llm/chat-library/ChatAnalytics';
import {ChatFileUpload} from 'llm/chat-library/ChatLibraryTypes';
import {getLatestSystemState} from 'llm/chat-library/ChatStateUtil';
import {setInitialChatData, getInitialChatData} from 'llm/chat-library/InitialChatData';
import {
  ChatEventNode,
  ClientChatEvent,
  ClientOptimisticUserMessage,
  ClientProgressMessage,
  ProgressStatus,
} from 'llm/chat-library/messages/MessageTypes';
import {createPendingUserMessage, createProgressMessage} from 'llm/chat-library/messages/MessageUtils';
import {useChatSessionEvents} from 'llm/chat-library/useChatSessionEvents';
import {runDataCaptureAssistant} from 'llm/data-capture/DataCaptureDAO';
import * as TaskRunner from 'util/TaskRunner';

export interface UseDataCaptureMessageThreadResult {
  events: Array<ClientChatEvent>;
  isLoading: boolean;
  isAssistantRunning: boolean;
  errorMessage: string | null;
  isLastMessageFromUser: boolean;
  sendMessage: (message: string, fileUploads: Array<ChatFileUpload>, retry?: boolean) => Promise<void>;
  refetch: () => void;
}

function getProgressMessage({
  hadFailure,
  serverEvents,
  pendingUserMessage,
}: {
  hadFailure: boolean;
  serverEvents: Array<ChatEventNode>;
  pendingUserMessage: ClientOptimisticUserMessage | null;
}): ClientProgressMessage | null {
  if (hadFailure) {
    return createProgressMessage({
      status: ProgressStatus.FAILED,
      errorMessage: null,
      agentType: AgentType.DATA_CAPTURE,
      progressData: null,
    });
  }
  const systemState = getLatestSystemState(serverEvents);
  const errorMessage = systemState?.error_message;
  if (errorMessage) {
    return createProgressMessage({
      status: ProgressStatus.FAILED,
      errorMessage,
      agentType: AgentType.DATA_CAPTURE,
      progressData: null,
    });
  }
  const lastDisplayEvent =
    pendingUserMessage ??
    serverEvents.findLast((event) => event.__typename === 'DisplayMessageChatEventNode');
  if (lastDisplayEvent && lastDisplayEvent.role === LLMRole.USER) {
    return createProgressMessage({
      status: ProgressStatus.STARTED,
      errorMessage: null,
      agentType: AgentType.DATA_CAPTURE,
      // TODO: Thread through progress data.
      progressData: null,
    });
  }
  return null;
}

export function useDataCaptureMessageThread({
  chatSessionId,
}: {
  chatSessionId: string;
}): UseDataCaptureMessageThreadResult {
  // If a chat was initiated via another component (e.g. NewChatInput), restore states for optimistic updates.
  // Notably, we will set the initial messages and isAssistantRunning.
  // We do not reset this state when chatId changes, as `ChatRouter` is rendered with a different `key` when
  // chat id changes.
  const initialChatData = getInitialChatData(chatSessionId);
  const initialMessage: ClientOptimisticUserMessage | null = useMemo(() => {
    if (!initialChatData) {
      return null;
    }
    return createPendingUserMessage({
      text: initialChatData.message.text,
      fileUploads: initialChatData.message.fileUploads,
      agentType: AgentType.DATA_CAPTURE,
    });
  }, [initialChatData]);

  const [pendingUserMessage, setPendingUserMessage] = useState<ClientOptimisticUserMessage | null>(
    initialMessage
  );
  const [isAssistantRunning, setIsAssistantRunning] = useState<boolean>(false);
  const [hadFailure, setHadFailure] = useState<boolean>(false);

  const {
    events: serverEvents,
    isLoading,
    errorMessage,
    threadStatus,
    refetch,
  } = useChatSessionEvents({
    chatSessionId,
    includeToolCalls: false /* currently expensive to fetch - and not yet used by Data Capture */,
    onEventsUpdated: (events) => {
      // Clear pending user message when we receive server events
      if (events.length > 0) {
        setPendingUserMessage(null);
      }
    },
  });

  useEffect(() => {
    const poll = async (): Promise<void> => {
      if (!initialChatData) {
        return;
      }
      setIsAssistantRunning(true);
      try {
        const statusUrl = await initialChatData.statusUrl;
        await TaskRunner.resumeTask(statusUrl);
        setInitialChatData(chatSessionId, null);
      } finally {
        setIsAssistantRunning(false);
      }
    };
    poll();
  }, [initialChatData, chatSessionId]);

  useEffect(() => {
    const isThreadActive = threadStatus === 'ACTIVE' || threadStatus === 'QUEUED';
    if (isThreadActive && !isAssistantRunning) {
      setIsAssistantRunning(true);
    } else if (!isThreadActive && isAssistantRunning && !pendingUserMessage) {
      setIsAssistantRunning(false);
    }
  }, [threadStatus, isAssistantRunning, pendingUserMessage]);

  // Handle sending message
  const sendMessage = useCallback(
    async (text: string, fileUploads: Array<ChatFileUpload>, retry: boolean = false) => {
      assert(pendingUserMessage == null, 'Cannot send message while pending user message is present');
      setPendingUserMessage(createPendingUserMessage({text, fileUploads, agentType: AgentType.DATA_CAPTURE}));
      setIsAssistantRunning(true);
      setHadFailure(false);

      try {
        trackMessageSent({
          chatType: ChatType.DATA_CAPTURE,
          agentType: AgentType.DATA_CAPTURE,
          messageCount: serverEvents.filter((event) => event.__typename === 'DisplayMessageChatEventNode')
            .length,
          fileCount: fileUploads.length,
          includePublicData: null,
        });
        await runDataCaptureAssistant(chatSessionId, text, fileUploads, retry);
      } catch (error) {
        setHadFailure(true);
        throw error;
      } finally {
        setIsAssistantRunning(false);
      }
    },
    [chatSessionId, serverEvents, pendingUserMessage]
  );

  const progressMessage = useMemo(
    () => getProgressMessage({hadFailure, serverEvents, pendingUserMessage}),
    [hadFailure, serverEvents, pendingUserMessage]
  );

  // Optimistically append the pending user message to the event list while we wait for the server to respond
  const events = useMemo<Array<ClientChatEvent>>(
    () => [
      ...serverEvents,
      ...(pendingUserMessage ? [pendingUserMessage] : []),
      ...(progressMessage ? [progressMessage] : []),
    ],
    [serverEvents, pendingUserMessage, progressMessage]
  );

  // Check if the chat is in an error state where the last message is from the user
  // and there's no assistant response
  const isLastMessageFromUser = useMemo(() => {
    const lastDisplayMessage = serverEvents.findLast(
      (event) => event.__typename === 'DisplayMessageChatEventNode'
    );
    return lastDisplayMessage != null && lastDisplayMessage.role === LLMRole.USER;
  }, [serverEvents]);

  return {
    events,
    isLoading,
    isAssistantRunning,
    errorMessage,
    isLastMessageFromUser,
    sendMessage,
    refetch,
  };
}
